// client/src/pages/Admin/ProjectManager/components/ProjectToolbar.tsx
/**
 * PROJECT TOOLBAR COMPONENT
 * 
 * Contains filters, search, and sort controls.
 * 
 * @module ProjectManager/Components/ProjectToolbar
 */

import React from 'react';
import { ArrowDown, ArrowUp, Filter, RotateCcw, Search, X } from 'lucide-react';
import { SORT_OPTIONS } from '../constants/project.constants';

interface ProjectToolbarProps {
    showFilters: boolean;
    onToggleFilters: () => void;
    activeFilterCount: number;
    hasActiveFilters: boolean;
    onResetFilters: () => void;
    sortOption: string;
    sortDirection: 'asc' | 'desc';
    onSortChange: (value: string) => void;
    onToggleSortDirection: () => void;
    searchTerm: string;
    onSearchChange: (value: string) => void;
    onClearSearch: () => void;
    isSearchFocused: boolean;
    onSearchFocus: () => void;
    onSearchBlur: () => void;
}

export const ProjectToolbar: React.FC<ProjectToolbarProps> = ({
    showFilters,
    onToggleFilters,
    activeFilterCount,
    hasActiveFilters,
    onResetFilters,
    sortOption,
    sortDirection,
    onSortChange,
    onToggleSortDirection,
    searchTerm,
    onSearchChange,
    onClearSearch,
    isSearchFocused,
    onSearchFocus,
    onSearchBlur
}) => {
    const sortLabel = SORT_OPTIONS.find(opt => opt.value === sortOption)?.label || 'Sort';

    return (
        <div className="toolbar-container">
            <div className="filter-bar">
                <div className="filter-bar-left">
                    <button
                        className={`filter-toggle ${showFilters ? 'active' : ''}`}
                        onClick={onToggleFilters}
                        aria-expanded={showFilters}
                        aria-label="Toggle filters"
                    >
                        <Filter size={14} />
                        Filters
                        {activeFilterCount > 0 && (
                            <span className="filter-badge">{activeFilterCount}</span>
                        )}
                    </button>

                    {hasActiveFilters && (
                        <button
                            className="reset-filters-btn"
                            onClick={onResetFilters}
                            aria-label="Reset all filters"
                        >
                            <RotateCcw size={14} />
                            Reset
                        </button>
                    )}
                </div>

                <div className="filter-bar-right">
                    <div className="sort-container">
                        <select
                            className="sort-select"
                            value={sortOption}
                            onChange={(e) => onSortChange(e.target.value)}
                            aria-label="Sort projects"
                        >
                            {SORT_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <button
                            className="sort-direction-btn"
                            onClick={onToggleSortDirection}
                            aria-label={`Sort ${sortDirection === 'asc' ? 'ascending' : 'descending'}`}
                            title={`Sort ${sortDirection === 'asc' ? 'ascending' : 'descending'}`}
                        >
                            {sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                        </button>
                    </div>

                    <div className={`search-box ${isSearchFocused ? 'focused' : ''}`}>
                        <Search size={14} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search projects..."
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            onFocus={onSearchFocus}
                            onBlur={onSearchBlur}
                            aria-label="Search projects"
                            className="search-input"
                        />
                        {searchTerm && (
                            <button
                                className="clear-search"
                                onClick={onClearSearch}
                                aria-label="Clear search"
                            >
                                <X size={12} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};